// Analytics wrapper for Home Cost Path — pre-launch privacy hardening.
//
// GUARANTEES:
// 1. Session replay is off (see index.html posthog.init options).
// 2. Only events in the ALLOWED_EVENTS set are ever sent to PostHog.
// 3. Only property keys in ALLOWED_PROPERTY_KEYS are ever forwarded. No raw
//    financial values (income, debts, cash, prices, credit scores, ZIP,
//    addresses, seller credits, etc.) are ever passed through.
// 4. If PostHog is not loaded (blocker / ad-block / dev), calls are no-ops.
//
// If you want to add a new event, add its name here AND document what
// properties it carries in memory/PRD.md.

const ALLOWED_EVENTS = new Set([
  "landing_view",
  "analysis_started",
  "analysis_completed",
  "account_created",
  "home_saved",
  "paywall_view",
  "checkout_started",
  "checkout_completed",
  "report_shared",
]);

// Non-sensitive property keys only. Nothing that reveals dollar amounts,
// income, debts, ZIP, address, credit-score range, or any raw worksheet
// value. Booleans and coarse categorical labels only.
const ALLOWED_PROPERTY_KEYS = new Set([
  "financing_type",        // "va" | "fha" | "conventional" | "usda" | "not_sure"
  "is_returning_user",     // boolean
  "plan_type",             // "free" | "premium"
  "reason",                // paywall reason code (e.g. "pdf_export")
  "source",                // "sample_va_nudge" | "manual" | etc
]);

function sanitizeProps(props) {
  if (!props || typeof props !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(props)) {
    if (!ALLOWED_PROPERTY_KEYS.has(k)) continue;
    // Coerce to string / boolean; never forward numbers that could be dollars.
    if (typeof v === "boolean") out[k] = v;
    else if (v == null) continue;
    else out[k] = String(v).slice(0, 64);
  }
  return out;
}

export function track(event, props) {
  try {
    if (!ALLOWED_EVENTS.has(event)) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[analytics] rejected non-allowlisted event:", event);
      }
      return;
    }
    if (typeof window === "undefined") return;
    const ph = window.posthog;
    if (!ph || typeof ph.capture !== "function") return;
    ph.capture(event, sanitizeProps(props));
  } catch { /* analytics must never break the app */ }
}

// Associate the current session with a stable, opaque user id. We do NOT
// pass email, name, or any other PII as person properties.
export function identify(userId) {
  try {
    if (!userId) return;
    const ph = typeof window !== "undefined" ? window.posthog : null;
    if (!ph || typeof ph.identify !== "function") return;
    ph.identify(String(userId));
  } catch { /* noop */ }
}

export function reset() {
  try {
    const ph = typeof window !== "undefined" ? window.posthog : null;
    if (ph && typeof ph.reset === "function") ph.reset();
  } catch { /* noop */ }
}
